import React, { FC, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import OvCompactButton from '../atoms/OvCompactButton';
import OvVisuallyHidden from '../atoms/OvVisuallyHidden';
import OvDiscardStagedFilesDialog from '../molecules/OvDiscardStagedFilesDialog';
import OvVideoDropZone from '../molecules/OvVideoDropZone';
import OvVideoStagingSubmitError from '../molecules/OvVideoStagingSubmitError';
import OvVideoStateNotice from '../molecules/OvVideoStateNotice';
import OvVideoUploadValidation from '../molecules/OvVideoUploadValidation';
import OvVideoStagingForm from './OvVideoStagingForm';
import { MAX_BULK_VIDEOS } from '../../../common/model/dto/video/video-constants';
import { VIDEO_UPLOAD_PATH } from '../../../common/model/ui/video-routes';
import { getStagingConsequence } from '../../../common/utils/video-staging-utils';
import { getLibrarySearch } from '../../../common/utils/video-route-utils';
import { useVideoStagingSession } from '../../../hooks/use-video-staging-session';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

type CloseReason = 'backdropClick' | 'escapeKeyDown';

const OvVideoStagingDialog: FC = () => {
  const { t } = useTranslation();
  const session = useVideoStagingSession();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const titleId = useId();
  const validationId = useId();
  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const isReview = !session.isEmpty;
  const { placedCount, unplacedCount } = getStagingConsequence(session.staged);

  const addFiles = (files: File[]) => {
    const { added } = session.addFiles(files);
    setAnnouncement(
      added ? t('videos.staging.filesAdded', { count: added }) : ''
    );
  };

  const requestClose = () => {
    if (session.isSubmitting) {
      return;
    }

    if (session.isEmpty) {
      session.closeDialog();
      return;
    }

    setIsConfirmingDiscard(true);
  };

  const editInBulk = () => {
    session.editInBulk(getLibrarySearch(pathname, search));
    navigate(VIDEO_UPLOAD_PATH);
  };

  const discardFiles = () => {
    setIsConfirmingDiscard(false);
    session.discard();
  };

  const handleClose = (_: unknown, reason: CloseReason) => {
    if (reason === 'backdropClick' && !session.isEmpty) {
      return;
    }

    requestClose();
  };

  return (
    <Dialog
      open={session.isDialogOpen && pathname !== VIDEO_UPLOAD_PATH}
      onClose={handleClose}
      fullWidth
      maxWidth={false}
      aria-labelledby={titleId}
      TransitionProps={{ onExited: () => setIsConfirmingDiscard(false) }}
      PaperProps={{
        style: {
          maxWidth: isReview
            ? Variables.layout.DIALOG_REVIEW_WIDTH
            : Variables.layout.DIALOG_COMPACT_WIDTH,
          backgroundColor: Colours.WHITE,
          borderRadius: Variables.borderRadius.LARGE,
        },
      }}
    >
      <Content>
        <Head>
          <h2 id={titleId}>{t('videos.staging.title')}</h2>
          <IconButton
            aria-label={t('videos.staging.close')}
            onClick={requestClose}
            disabled={session.isSubmitting}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Head>

        <Body>
          {isReview ? (
            <OvVideoStagingForm onFiles={addFiles} />
          ) : (
            <OvVideoDropZone
              label={t('videos.staging.chooseFiles')}
              buttonLabel={t('videos.staging.chooseFiles')}
              hint={t('videos.staging.dropHere')}
              limits={t('videos.staging.limits', { max: MAX_BULK_VIDEOS })}
              isMultiple
              isDisabled={false}
              isButtonFocused
              onFiles={addFiles}
            />
          )}
        </Body>

        <Footer>
          {isReview && session.staged.length > 0 && (
            <OvVideoStateNotice
              notice={{ kind: 'stagedBatch', placedCount, unplacedCount }}
              variant="compact"
            />
          )}
          {isReview && (
            <OvVideoUploadValidation
              id={validationId}
              blocker={session.blocker}
              readyCount={session.staged.length}
            />
          )}
          <OvVideoStagingSubmitError
            error={session.submitError}
            detail={session.submitErrorDetail}
          />
          <Actions>
            {isReview && (
              <BulkAction>
                <OvCompactButton
                  tone="secondary"
                  disabled={session.isSubmitting}
                  onClick={editInBulk}
                >
                  {t('videos.staging.editInBulk')}
                </OvCompactButton>
              </BulkAction>
            )}
            <OvCompactButton
              tone="secondary"
              onClick={requestClose}
              disabled={session.isSubmitting}
            >
              {t('common.actions.cancel')}
            </OvCompactButton>
            {isReview && (
              <OvCompactButton
                tone="primary"
                isBusy={session.isSubmitting}
                busyLabel={t('videos.staging.submitting')}
                disabled={session.blocker.kind !== 'none'}
                aria-describedby={validationId}
                onClick={() => session.submit()}
              >
                {t('videos.staging.submit', { count: session.staged.length })}
              </OvCompactButton>
            )}
          </Actions>
        </Footer>
      </Content>

      <OvDiscardStagedFilesDialog
        isOpen={isConfirmingDiscard}
        onKeep={() => setIsConfirmingDiscard(false)}
        onDiscard={discardFiles}
      />

      <OvVisuallyHidden role="status">{announcement}</OvVisuallyHidden>
    </Dialog>
  );
};

export default OvVideoStagingDialog;

const Content = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1rem 0.5rem 1.5rem;
  color: ${Colours.OV_BASE};

  h2 {
    margin: 0;
    font-size: ${Variables.fontSizes.MLARGE};
    line-height: ${Variables.lineHeights.MLARGE};
  }
`;

const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0.5rem 1.5rem 1rem;
  color: ${Colours.OV_BASE};
  font-size: ${Variables.fontSizes.MEDIUM};
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem 1.5rem;
  border-top: 1px solid ${Colours.OV_BORDER_COLOR_V3};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.75rem;
`;

const BulkAction = styled.div`
  margin-right: auto;
`;
